/**
 * Utility functions for handling API errors with user-friendly messages
 */

export interface ApiErrorInfo {
  userMessage: string;
  shouldReload?: boolean;
  reloadDelay?: number;
}

/**
 * Parse API error and return user-friendly message
 */
export function parseApiError(error: any): ApiErrorInfo {
  const responseData = error.response?.data;

  // Item already reserved - exact match for API response
  if (responseData?.error === "ItemAlreadyReservedException") {
    return {
      userMessage:
        "Questo regalo è già stato prenotato da qualcun altro. La lista verrà aggiornata.",
      shouldReload: true,
      reloadDelay: 2000,
    };
  }

  // Reservation not found
  if (responseData?.error === "ReservationNotFound") {
    return {
      userMessage: "La prenotazione non esiste più. La lista verrà aggiornata.",
      shouldReload: true,
      reloadDelay: 2000,
    };
  }

  // Item not found
  if (
    responseData?.error === "ItemNotFound" ||
    error.response?.status === 404
  ) {
    return {
      userMessage: error.response?.data?.message || 
        "Il regalo non è più disponibile. La lista verrà aggiornata.",
      shouldReload: true,
      reloadDelay: 2000,
    };
  }

  // Permission denied
  if (
    responseData?.error === "PermissionDenied" ||
    error.response?.status === 403
  ) {
    let userMessage = error.response?.data?.message;
    if (userMessage.includes('Reservation period has not started yet')) {
      userMessage = "Ops! Il periodo di prenotazione dei regali non è ancora iniziato.";
    }
    if (!userMessage) {
      userMessage = "Non hai i permessi per eseguire questa azione.";
    }
    return {
      userMessage: userMessage,
      shouldReload: false,
    };
  }

  // Network/server errors
  if (error.response?.status >= 500) {
    return {
      userMessage: "Errore del server. Riprova tra qualche minuto.",
      shouldReload: false,
    };
  }

  // Network error (no response)
  if (!error.response) {
    return {
      userMessage:
        "Errore di connessione. Controlla la tua connessione internet.",
      shouldReload: false,
    };
  }

  // Generic error
  return {
    userMessage: "Si è verificato un errore. Riprova.",
    shouldReload: false,
  };
}

/**
 * Handle API error with automatic reload if needed
 */
export function handleApiError(
  error: any,
  setError: (message: string | null) => void,
  reloadFunction?: () => void
): void {
  const errorInfo = parseApiError(error);

  // console.error("API Error:", error);
  setError(errorInfo.userMessage);

  if (errorInfo.shouldReload && reloadFunction) {
    setTimeout(() => {
      reloadFunction();
      setError(null);
    }, errorInfo.reloadDelay || 2000);
  }
}
